import { cn, ToggleGroupItem } from "@aircall/ds";
import { type ComponentPropsWithoutRef, type Key, type ReactNode } from "react";
import { InputChoiceContextProvider } from "../../contexts/InputChoiceContext/InputChoiceContext";
import { useInputChoices } from "../../contexts/useInputChoices";
import type { Mode } from "../../InputChoices";
import { InputChoiceInput } from "../InputChoiceInput/InputChoiceInput";

// Resolved visual tone for a choice. The concrete surface colour is painted in
// the app's `src/style.css`, keyed on the matching `data-tone` attribute:
// @aircall/ds ships its bg-* token utilities only as precompiled classes, so the
// app's Tailwind can't synthesise the pressed-state override needed to beat the
// DS toggle's own `data-pressed:bg-secondary`.
export type Tone = "positive" | "negative" | "neutral" | "none" | "disabled";

export interface InputChoiceProps<Value extends Key>
  extends Omit<
    ComponentPropsWithoutRef<typeof ToggleGroupItem>,
    "children" | "value"
  > {
  children: ReactNode | ReactNode[];
  value: Value;
  disabled?: boolean;
  name?: string;
  "data-test"?: string;
  /**
   * Position of this choice among its siblings. Injected by `InputChoices`
   * (consumers do not set it). Used to resolve the binary tone: index 0 is the
   * positive (green) choice, the last index is the negative (red) choice.
   */
  index?: number;
}

export function resolveTone({
  mode,
  active,
  disabled,
  index,
}: {
  mode: Mode;
  active: boolean;
  disabled: boolean;
  index: number;
}): Tone {
  // Disabled always wins so a locked choice reads as muted, never coloured.
  if (disabled) {
    return "disabled";
  }

  if (!active) {
    return "none";
  }

  // Binary: the first choice is the positive (green) answer, every later
  // choice is the negative (red) answer.
  if (mode === "binary") {
    return index === 0 ? "positive" : "negative";
  }

  // Multiple -> neutral (charcoal) surface.
  return "neutral";
}

export function InputChoice<Value extends Key>({
  children,
  value,
  name,
  disabled = false,
  index = 0,
  "data-test": dataTest = `input-choice-${value}`,
  className,
  ...props
}: InputChoice.Props<Value>) {
  const { mode, value: selectedValue } = useInputChoices();
  const active = value === selectedValue;

  const tone = resolveTone({ mode, active, disabled, index });

  return (
    <InputChoiceContextProvider isDisabled={disabled} isSelected={active}>
      <ToggleGroupItem
        data-test={dataTest}
        // Keep the DS primitive's own `data-slot="toggle-group-item"` — the
        // app's `style.css` tone rules key on it; overriding it drops the
        // selected-surface colour.
        // The selected-surface colour is painted in style.css from this tone
        // (see the `[data-tone]` rules); DS token bg-* utilities can't be
        // re-synthesised by the app's Tailwind for the pressed state.
        data-tone={tone}
        value={String(value)}
        disabled={disabled}
        className={cn(
          "flex-1 gap-1 p-2",
          // DS's toggle ships `data-disabled:pointer-events-none`, which hides
          // our not-allowed cursor. Re-enable pointer events (with `!important`
          // to beat that compiled `[data-disabled]` rule) so the cursor shows;
          // the item stays inert via its native `disabled` attribute. The muted
          // surface is a DS precompiled `bg-muted` utility (plain, so it works).
          disabled
            ? "bg-muted cursor-not-allowed pointer-events-auto!"
            : "cursor-pointer",
          // Border illusion between elements.
          "not-last:border-r not-last:border-border",
          className
        )}
        {...props}
      >
        {children}
        <InputChoiceInput
          name={name}
          value={value}
          active={active}
          disabled={disabled}
        />
      </ToggleGroupItem>
    </InputChoiceContextProvider>
  );
}

export namespace InputChoice {
  export type Props<Value extends Key> = InputChoiceProps<Value>;
}
